import { useState } from "react";
import {
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BookOpenIcon,
  PhoneIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    {
      id: "all",
      name: "All Topics",
      icon: <BookOpenIcon className="w-5 h-5" />,
    },
    {
      id: "getting-started",
      name: "Getting Started",
      icon: <CheckCircleIcon className="w-5 h-5" />,
    },
    {
      id: "account",
      name: "Account & Profile",
      icon: <InformationCircleIcon className="w-5 h-5" />,
    },
    {
      id: "donations",
      name: "Donations",
      icon: <ExclamationTriangleIcon className="w-5 h-5" />,
    },
    {
      id: "technical",
      name: "Technical Issues",
      icon: <QuestionMarkCircleIcon className="w-5 h-5" />,
    },
  ];

  const faqs = [
    {
      id: 1,
      category: "getting-started",
      question: "How do I get started with MealBridge?",
      answer:
        "Getting started is easy! Simply create an account, choose your role (donor, recipient, or volunteer), and complete your profile. You can then start browsing available food listings or posting your own donations.",
    },
    {
      id: 2,
      category: "getting-started",
      question: "What are the different user roles?",
      answer:
        "MealBridge has three main roles: Donors (restaurants, bakeries, individuals who share food), Recipients (people who need food assistance), and Volunteers (people who help with deliveries and coordination).",
    },
    {
      id: 3,
      category: "account",
      question: "How do I update my profile information?",
      answer:
        "Go to your dashboard and click on 'Profile Settings'. You can update your personal information, contact details, and preferences at any time.",
    },
    {
      id: 4,
      category: "account",
      question: "Can I change my user role?",
      answer:
        "Yes, you can change your role by going to Profile Settings and selecting a new role. Note that changing roles may require re-verification of your account.",
    },
    {
      id: 5,
      category: "donations",
      question: "What types of food can I donate?",
      answer:
        "You can donate any safe, edible food including prepared meals, fresh produce, packaged goods, and bakery items. All food must be within safe consumption dates and properly stored.",
    },
    {
      id: 6,
      category: "donations",
      question: "How do I ensure food safety when donating?",
      answer:
        "Always follow proper food handling guidelines, maintain appropriate temperatures, clearly label allergens, and provide accurate descriptions. Use our food safety checklist for guidance.",
    },
    {
      id: 7,
      category: "donations",
      question: "What happens if no one claims my donation?",
      answer:
        "If your donation isn't claimed within the specified timeframe, you can extend the listing, modify the details, or remove it. We'll notify you before the expiration time.",
    },
    {
      id: 8,
      category: "technical",
      question: "The app is running slowly. What should I do?",
      answer:
        "Try refreshing the page, clearing your browser cache, or updating to the latest version. If issues persist, contact our technical support team.",
    },
    {
      id: 9,
      category: "technical",
      question: "I'm having trouble uploading photos. What's wrong?",
      answer:
        "Ensure your photos are in JPG, PNG, or WebP format and under 5MB. Check your internet connection and try again. If problems continue, contact support.",
    },
    {
      id: 10,
      category: "technical",
      question: "How do I report a bug or technical issue?",
      answer:
        "Use the 'Report an Issue' button in your dashboard or contact our technical support team directly. Please include details about what you were doing when the issue occurred.",
    },
  ];

  const helpTopics = [
    {
      title: "Getting Started Guide",
      description: "Complete walkthrough for new users",
      icon: <BookOpenIcon className="w-6 h-6" />,
      articles: 5,
    },
    {
      title: "Food Safety Guidelines",
      description: "Best practices for safe food handling",
      icon: <ExclamationTriangleIcon className="w-6 h-6" />,
      articles: 8,
    },
    {
      title: "Account Management",
      description: "Managing your profile and settings",
      icon: <InformationCircleIcon className="w-6 h-6" />,
      articles: 6,
    },
    {
      title: "Troubleshooting",
      description: "Common issues and solutions",
      icon: <QuestionMarkCircleIcon className="w-6 h-6" />,
      articles: 12,
    },
  ];

  const contactMethods = [
    {
      title: "Live Chat",
      description: "Get instant help from our support team",
      contact: "Click the chat bubble",
      availability: "Mon-Fri, 9:00 AM - 6:00 PM",
    },
    {
      title: "Email Support",
      description: "Send us a detailed message",
      icon: <EnvelopeIcon className="w-6 h-6" />,
      availability: "24/7 - Response within 24 hours",
    },
    {
      title: "Phone Support",
      description: "Speak directly with our team",
      icon: <PhoneIcon className="w-6 h-6" />,
      availability: "Mon-Fri, 9:00 AM - 6:00 PM",
    },
  ];

  const toggleExpanded = (id: number) => {
    setExpandedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const filteredFaqs =
    selectedCategory === "all"
      ? faqs
      : faqs.filter((faq) => faq.category === selectedCategory);

  const searchResults = searchQuery
    ? faqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredFaqs;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-2xl shadow-2xl border-b border-white/30">
        <div className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center">
            <QuestionMarkCircleIcon className="w-16 h-16 mx-auto mb-6 text-emerald-600" />
            <h1 className="mb-6 text-3xl font-bold text-slate-900 md:text-4xl lg:text-5xl">
              Help Center
            </h1>
            <p className="max-w-3xl mx-auto text-lg text-slate-600 font-medium md:text-xl">
              Find answers to common questions and get the support you need
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Search Bar */}
        <div className="mb-12">
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-y-0 left-0 flex items-center pl-4">
              <MagnifyingGlassIcon className="w-5 h-5 text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Search for help articles, FAQs, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-4 pl-12 pr-4 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Categories */}
        <section className="mb-12">
          <h2 className="mb-6 text-2xl font-bold text-center text-slate-900">
            Browse by Category
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? "bg-emerald-600 text-white"
                    : "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50"
                }`}
              >
                {category.icon}
                {category.name}
              </button>
            ))}
          </div>
        </section>

        {/* Help Topics */}
        <section className="mb-16">
          <h2 className="mb-8 text-2xl font-bold text-center text-slate-900">
            Popular Help Topics
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {helpTopics.map((topic, index) => (
              <div key={index} className="card-dashboard">
                <div className="flex items-center mb-4 space-x-3">
                  <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                    {topic.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {topic.title}
                  </h3>
                </div>
                <p className="mb-3 text-sm text-slate-600">
                  {topic.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    {topic.articles} articles
                  </span>
                  <Link
                    to="/help"
                    className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
                  >
                    View Articles →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="mb-8 text-2xl font-bold text-center text-slate-900">
            Frequently Asked Questions
          </h2>
          <div className="max-w-4xl mx-auto space-y-4">
            {searchResults.map((faq) => (
              <div key={faq.id} className="card-dashboard">
                <button
                  onClick={() => toggleExpanded(faq.id)}
                  className="flex items-center justify-between w-full p-4 text-left"
                >
                  <h3 className="text-lg font-semibold text-slate-900">
                    {faq.question}
                  </h3>
                  {expandedItems.includes(faq.id) ? (
                    <ChevronUpIcon className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-slate-500" />
                  )}
                </button>
                {expandedItems.includes(faq.id) && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Contact Support */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="mb-4 text-2xl font-bold text-slate-900">
              Still Need Help?
            </h2>
            <p className="text-base text-slate-600">
              Can't find what you're looking for? Our support team is here to
              help.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {contactMethods.map((method, index) => (
              <div key={index} className="text-center card-dashboard">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-lg bg-emerald-100 text-emerald-600">
                  {method.icon}
                </div>
                <h3 className="mb-2 text-lg font-semibold text-slate-900">
                  {method.title}
                </h3>
                <p className="mb-3 text-sm text-slate-600">
                  {method.description}
                </p>
                <div className="flex items-center justify-center space-x-1 text-xs text-slate-500">
                  <span>{method.availability}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <div className="card-dashboard bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200">
            <h2 className="mb-4 text-xl font-bold text-slate-900">
              Ready to Get Started?
            </h2>
            <p className="max-w-2xl mx-auto mb-6 text-sm text-slate-700">
              Join thousands of people making a difference in their communities
              through food sharing.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/auth"
                className="px-6 py-3 text-sm font-medium text-white bg-emerald-600 rounded-lg border border-emerald-600 text-center"
              >
                Sign Up Now
              </Link>
              <Link
                to="/contact"
                className="px-6 py-3 text-sm font-medium text-slate-700 bg-white rounded-lg border border-slate-300 text-center"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
